import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { type CarouselRef } from 'antd5/es/carousel';
import { useRef, useState } from 'react';

import EmptyState from 'components/EmptyState';
import { Button } from 'components/ui';
import {
  type PreviousDamageInfo,
  type PreviousInspectionInfo,
} from 'horizon/components/Damages/HorizonDamageImage/types';

import { type ImageThumbnailProps, ImageThumbnail } from './ImageThumbnail';
import { CarouselContainer, EmptyImage, StyledCarousel } from './ImageThumbnail.style';

type ThumbnailCarouselProps = {
  thumbnails: {
    [K in keyof Omit<ImageThumbnailProps, 'handleClickObservation' | 'allowZoom'>]?:
      | ImageThumbnailProps[K]
      | null
      | undefined;
  }[]; // Partial<T>, but allow null | undefined (to allow for null or undefined picture, which is handled when rendering)
  initialSlideIndex?: number;
  allowZoom?: boolean;
  shouldShowSeverity?: boolean;
  shouldShowDistance?: boolean;
  shouldShowExpand?: boolean;
  previousDamageInfo?: PreviousDamageInfo;
  prevInspectionDetails?: PreviousInspectionInfo;
  handleCompareNavigation?: (val?: boolean) => void;
  compareInspectionPicturesLoading?: boolean;
};

export const ThumbnailCarousel: React.FunctionComponent<ThumbnailCarouselProps> = ({
  thumbnails,
  initialSlideIndex,
  allowZoom = false,
  shouldShowSeverity,
  shouldShowDistance,
  shouldShowExpand,
  previousDamageInfo,
  prevInspectionDetails,
  handleCompareNavigation,
  compareInspectionPicturesLoading,
}) => {
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlideIndex ?? 0);

  return (
    <CarouselContainer>
      {thumbnails.length > 1 && (
        <>
          <Button
            className="prev-button"
            title="Previous Image"
            disabled={currentSlide === 0}
            icon={<LeftOutlined />}
            onClick={() => {
              carouselRef.current?.prev();
            }}
          />
          <Button
            className="next-button"
            title="Next Image"
            disabled={currentSlide === thumbnails.length - 1}
            icon={<RightOutlined />}
            onClick={() => {
              carouselRef.current?.next();
            }}
          />
        </>
      )}
      <StyledCarousel
        ref={carouselRef}
        initialSlide={initialSlideIndex ?? 0}
        afterChange={setCurrentSlide}
        dots={thumbnails.length > 1}
      >
        {thumbnails.map((thumbnailProps, i) =>
          !!thumbnailProps.picture ? (
            <ImageThumbnail
              key={i}
              {...(thumbnailProps as Omit<ImageThumbnailProps, 'handleClickObservation'>)}
              allowZoom={allowZoom}
              shouldShowSeverity={shouldShowSeverity}
              shouldShowDistance={shouldShowDistance}
              shouldShowExpand={shouldShowExpand}
              $isInCarousel={thumbnails.length > 1}
              previousDamageInfo={previousDamageInfo}
              prevInspectionDetails={prevInspectionDetails}
              handleCompareNavigation={handleCompareNavigation}
              compareInspectionPicturesLoading={compareInspectionPicturesLoading}
            />
          ) : (
            <EmptyImage key={i}>
              <EmptyState text="No Image Available" />
            </EmptyImage>
          )
        )}
      </StyledCarousel>
    </CarouselContainer>
  );
};
